import { useDocumentEditor } from "../../components/document-editor/DocumentEditorContext";
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Code2,
  LoaderCircle,
  MessageSquare,
  PanelRightClose,
  PanelLeft,
} from "lucide-react";
import type { Project } from "@lumen/shared";
import { api } from "../../api";
import { t } from "../../i18n";
import type { DisplayConversation } from "./ChatPage.types";
import type { WorkspaceEditorSelection } from "../../components/workspace-editor/workspace-editor-types";
import type { DevelopmentSelection } from "../../components/chat/ChatDevelopmentPanel.types";
import { beginPointerResize } from "../../components/shared/pointer-resize";
import {
  conversationViewPath,
  rememberProjectView,
  type ProjectView,
} from "../../components/projects/projectViewPreference";
import { PROJECTS_CHANGED_EVENT } from "../../components/projects/projectEvents";

const WorkspaceEditor = lazy(() =>
  import("../../components/workspace-editor/WorkspaceEditor").then(
    (module) => ({
      default: module.WorkspaceEditor,
    }),
  ),
);

export function ConversationWorkspace({
  conversation,
  workspaceId,
  repositoryId,
  children,
  developmentPanel,
  onWorkspaceSelectionChange,
  onToggleSidebar,
  onAddToChat,
}: {
  conversation: DisplayConversation | null;
  workspaceId: string | null;
  repositoryId: string | null;
  children: ReactNode;
  onToggleSidebar?: () => void;
  developmentPanel?: ReactNode;
  onWorkspaceSelectionChange: (selection: WorkspaceEditorSelection) => void;
  onAddToChat: (
    path: string,
    selection?: string,
    source?: DevelopmentSelection,
  ) => void;
}) {
  const conversationId = conversation?.id;
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const documentOpen = useDocumentEditor()?.active === true;
  const editorOpen = !documentOpen && /\/workspace\/?$/.test(location.pathname);
  const projectRoute = location.pathname.startsWith("/projects/");
  const projectId = projectRoute
    ? (conversation?.groupId ?? query.get("project"))
    : null;
  const [project, setProject] = useState<Project | null>(null);
  const [visitedEditor, setVisitedEditor] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [chatOpen, setChatOpen] = useState(() => {
    try {
      return localStorage.getItem("lumen:workspace-chat-open") === "true";
    } catch {
      return false;
    }
  });
  const [chatWidth, setChatWidth] = useState(() => {
    try {
      return Math.max(
        300,
        Math.min(
          800,
          Number(localStorage.getItem("lumen:workspace-chat-width")) || 360,
        ),
      );
    } catch {
      return 360;
    }
  });
  useEffect(() => {
    if (editorOpen && conversationId) setVisitedEditor(conversationId);
  }, [editorOpen, conversationId]);
  useEffect(() => {
    if (!documentOpen && projectId && conversationId)
      rememberProjectView(projectId, editorOpen ? "editor" : "chat");
  }, [projectId, editorOpen, conversationId, documentOpen]);
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    void api<Project>(`/projects/${projectId}`)
      .then((value) => {
        if (!cancelled) setProject(value);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new ResizeObserver(([entry]) =>
      setAvailableWidth(entry.contentRect.width),
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem("lumen:workspace-chat-open", String(chatOpen));
      localStorage.setItem("lumen:workspace-chat-width", String(chatWidth));
    } catch {
      /* Storage is optional. */
    }
  }, [chatOpen, chatWidth]);

  const switchView = async (view: ProjectView) => {
    setError("");
    if (conversation) {
      if (projectId) rememberProjectView(projectId, view);
      if ((view === "editor") === editorOpen) return;
      navigate(conversationViewPath(location.pathname, location.search, view));
      return;
    }
    if (view !== "editor" || !projectId || opening) return;
    setOpening(true);
    try {
      // Opening Editor explicitly starts a project session, even before its first prompt.
      const created = await api<DisplayConversation>("/conversations", {
        method: "POST",
        body: JSON.stringify({ surface: "project", groupId: projectId }),
      });
      window.dispatchEvent(new Event(PROJECTS_CHANGED_EVENT));
      navigate(`/projects/c/${created.id}/workspace`, { replace: true });
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : t("Could not open editor"),
      );
    } finally {
      setOpening(false);
    }
  };
  const splitChat = editorOpen && chatOpen && availableWidth >= 1040;
  const visibleChatWidth = Math.min(
    chatWidth,
    Math.max(300, availableWidth - 640),
  );
  const title =
    project?.id === projectId
      ? project.name
      : projectId
        ? t("Project")
        : conversation?.title;
  const queryWorkspaceId = query.get("workspace");
  // The switch only makes sense once a workspace exists, so plain chats
  // without one dismiss the whole header. Project sessions may still be
  // started editor-first (before any workspace exists), so project routes
  // keep the header.
  const hasWorkspace = Boolean(workspaceId || queryWorkspaceId || projectId);
  const hasHeader =
    !documentOpen && hasWorkspace && Boolean(conversation || projectId);

  return (
    <div
      ref={rootRef}
      className="flex min-h-0 min-w-0 flex-1 flex-col"
      data-conversation-workspace=""
    >
      {hasHeader && (
        <header className="flex min-h-9 shrink-0 flex-wrap items-center gap-x-3 gap-y-0.5 border-b border-stone-200 bg-white px-3 py-1 sm:px-5 dark:border-white/10 dark:bg-[#171b1a]">
          {onToggleSidebar && (
            <button
              type="button"
              onClick={onToggleSidebar}
              aria-label={t("Toggle sidebar")}
              title={t("Toggle sidebar")}
              className="hidden size-8 shrink-0 place-items-center rounded-lg text-stone-600 hover:bg-stone-100 focus-visible:ring-2 focus-visible:ring-lumen-500 lg:grid dark:text-stone-300 dark:hover:bg-white/10"
            >
              <PanelLeft size={16} aria-hidden="true" />
            </button>
          )}
          <div className="min-w-0 flex-1 basis-24">
            <div className="truncate text-sm font-semibold" title={title}>
              {title}
            </div>
            {projectId && conversation && (
              <div
                className="truncate text-xs text-stone-500 dark:text-stone-400"
                title={conversation.title}
              >
                {conversation.title}
              </div>
            )}
          </div>
          <nav
            aria-label={t("Project views")}
            className="flex shrink-0 items-center rounded-lg bg-stone-100 p-0.5 dark:bg-white/5"
          >
            {(
              [
                ["chat", MessageSquare, t("Chat")],
                ["editor", Code2, t("Editor")],
              ] as const
            ).map(([view, Icon, label]) => {
              const selected = (view === "editor") === editorOpen;
              return (
                <button
                  key={view}
                  type="button"
                  aria-pressed={selected}
                  disabled={opening}
                  onClick={() => void switchView(view)}
                  className={`inline-flex min-h-7 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-lumen-500 focus-visible:outline-none disabled:opacity-60 ${selected ? "bg-white text-lumen-800 shadow-sm dark:bg-white/10 dark:text-lumen-200" : "text-stone-600 hover:bg-white/60 active:bg-white dark:text-stone-300 dark:hover:bg-white/5 dark:active:bg-white/10"}`}
                >
                  {view === "editor" && opening ? (
                    <LoaderCircle
                      size={14}
                      className="animate-spin motion-reduce:animate-none"
                      aria-hidden="true"
                    />
                  ) : (
                    <Icon size={14} aria-hidden="true" />
                  )}
                  {label}
                </button>
              );
            })}
          </nav>
        </header>
      )}
      {error && (
        <div
          role="alert"
          className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
        >
          {error}
        </div>
      )}
      <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <div
          hidden={!editorOpen}
          className={`${editorOpen ? "flex" : "hidden"} min-h-0 min-w-0 flex-1`}
        >
          {conversation &&
            (editorOpen || visitedEditor === conversation.id) && (
              <Suspense
                fallback={
                  <div
                    role="status"
                    className="grid flex-1 place-items-center text-sm text-stone-500"
                  >
                    {t("Loading editor…")}
                  </div>
                }
              >
                <WorkspaceEditor
                  key={conversation.id}
                  conversationId={conversation.id}
                  active={editorOpen}
                  initialWorkspaceId={query.get("workspace") ?? workspaceId}
                  initialRepositoryId={
                    query.has("workspace")
                      ? query.get("repository")
                      : repositoryId
                  }
                  initialPath={query.get("file") ?? undefined}
                  onBack={() => void switchView("chat")}
                  chatOpen={splitChat}
                  onToggleChat={() => {
                    if (availableWidth < 1040) void switchView("chat");
                    else setChatOpen((value) => !value);
                  }}
                  onWorkspaceSelectionChange={onWorkspaceSelectionChange}
                  onAddToChat={(...args) => {
                    onAddToChat(...args);
                    if (availableWidth < 1040) void switchView("chat");
                    else setChatOpen(true);
                  }}
                />
              </Suspense>
            )}
        </div>
        {/* A stable sibling keeps the composer, transcript and scroll DOM alive in both views. */}
        <section
          aria-label={t("Conversation")}
          hidden={editorOpen && !splitChat}
          className={`${editorOpen && !splitChat ? "hidden" : "flex"} relative min-h-0 min-w-0 flex-col ${splitChat ? "shrink-0 border-l border-stone-200 dark:border-white/10" : "flex-1"}`}
          style={splitChat ? { width: visibleChatWidth } : undefined}
        >
          {splitChat && (
            <>
              <button
                type="button"
                aria-label={t("Resize conversation panel")}
                title={t("Drag to resize, or use arrow keys")}
                onPointerDown={(event) =>
                  beginPointerResize(event, {
                    axis: "x",
                    cursor: "col-resize",
                    initialValue: visibleChatWidth,
                    minimum: 300,
                    maximum: Math.min(800, availableWidth - 640),
                    onValueChange: setChatWidth,
                  })
                }
                onKeyDown={(event) => {
                  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
                    return;
                  event.preventDefault();
                  setChatWidth(
                    Math.max(
                      300,
                      Math.min(
                        availableWidth - 640,
                        800,
                        visibleChatWidth +
                          (event.key === "ArrowLeft" ? 24 : -24),
                      ),
                    ),
                  );
                }}
                className="absolute inset-y-0 -left-1.5 z-10 w-3 cursor-col-resize touch-none focus-visible:bg-lumen-300/50"
              />
              <div className="flex min-h-11 shrink-0 items-center justify-between border-b border-stone-200 px-3 text-xs font-semibold dark:border-white/10">
                {t("Conversation")}
                <button
                  type="button"
                  onClick={() => setChatOpen(false)}
                  aria-label={t("Close chat panel")}
                  className="grid size-11 place-items-center rounded-lg text-stone-600 hover:bg-stone-100 focus-visible:ring-2 focus-visible:ring-lumen-500 dark:text-stone-300 dark:hover:bg-white/10"
                >
                  <PanelRightClose size={16} aria-hidden="true" />
                </button>
              </div>
            </>
          )}
          {children}
        </section>
        {!editorOpen && !documentOpen && developmentPanel}
      </div>
    </div>
  );
}
